import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  FileText, 
  TrendingUp, 
  DollarSign, 
  PlusCircle
} from 'lucide-react'
import { obterEstatisticas, listarEmendas } from '../../services/emendasService'
import { formatarMoeda, formatarData } from '../../lib/admin-supabase'

interface Estatisticas {
  total: number
  ativas: number
  inativas: number
  rascunhos: number
  valorTotal: number
  criadasEsteMes: number
}

interface EmendaRecente {
  id: string
  titulo: string
  valor_emenda: number
  estado: string
  created_at: string
  status: string
}

export const AdminDashboard: React.FC = () => {
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null)
  const [emendasRecentes, setEmendasRecentes] = useState<EmendaRecente[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    carregarDados()
  }, [])

  const carregarDados = async () => {
    try {
      const [statsData, emendasData] = await Promise.all([
        obterEstatisticas(),
        listarEmendas({ limit: 5 })
      ])
      
      setEstatisticas(statsData)
      setEmendasRecentes(emendasData)
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Visão geral das emendas parlamentares
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total de Emendas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {estatisticas?.total || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Emendas Ativas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {estatisticas?.ativas || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Valor Total
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatarMoeda(estatisticas?.valorTotal || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PlusCircle className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Criadas este Mês
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {estatisticas?.criadasEsteMes || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Emendas */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Emendas Recentes
            </h3>
            <Link
              to="/admin/emendas"
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Ver todas
            </Link>
          </div>
          
          {emendasRecentes.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              Nenhuma emenda encontrada
            </p>
          ) : (
            <div className="flow-root">
              <ul className="-my-5 divide-y divide-gray-200">
                {emendasRecentes.map((emenda) => (
                  <li key={emenda.id} className="py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {emenda.titulo}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatarMoeda(emenda.valor_emenda)} • {emenda.estado}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          emenda.status === 'ativo' 
                            ? 'bg-green-100 text-green-800'
                            : emenda.status === 'inativo'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {emenda.status}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
